/**
 * API Overview documentation page
 */

import {DocsContent} from '../../../components/docs/DocsContent';
import {CodeBlock} from '../../../components/docs/CodeBlock';
import {DocsActionButtons} from '../../../components/docs/DocsActionButtons';
import {Link} from 'react-router-dom';
import {ROUTES} from '../../../config/constants';

export function APIOverview() {
  return (
    <DocsContent
      title="API Overview"
      description="Integrate VoxaPilot into your application using our REST API."
    >
      <div className="space-y-8">
        <section>
          <h2 className="text-2xl font-bold text-textPrimary mb-4">Introduction</h2>
          <p className="text-textSecondary mb-4">
            The VoxaPilot API is a RESTful API that allows you to programmatically access and
            manage meetings, transcripts, summaries, and more. All API requests use JSON and
            require authentication.
          </p>
        </section>

        <section>
          <h2 className="text-2xl font-bold text-textPrimary mb-4">Base URL</h2>
          <CodeBlock code="https://api.voxapilot.com/v1" language="text" />
        </section>

        <section>
          <h2 className="text-2xl font-bold text-textPrimary mb-4">Authentication</h2>
          <p className="text-textSecondary mb-4">
            All API requests require authentication using an API key. Include your API key in the
            Authorization header:
          </p>

          <CodeBlock
            code={`Authorization: Bearer YOUR_API_KEY`}
            language="text"
          />

          <p className="text-textSecondary dark:text-[#858585] mt-4 mb-4">
            Get your API key from{' '}
            <Link to={ROUTES.APP_SETTINGS} className="text-gray-900 dark:text-orange-500 underline hover:text-gray-700 dark:hover:text-orange-400">
              Settings → API Keys
            </Link>
            .
          </p>
        </section>

        <section>
          <h2 className="text-2xl font-bold text-textPrimary mb-4">Rate Limits</h2>
          <p className="text-textSecondary mb-4">
            API requests are rate-limited based on your plan:
          </p>

          <ul className="list-disc list-inside space-y-2 text-textSecondary mb-6">
            <li>Free: 100 requests/hour</li>
            <li>Pro: 1,000 requests/hour</li>
            <li>Business: 10,000 requests/hour</li>
            <li>Enterprise: Custom limits</li>
          </ul>
        </section>

        <section>
          <h2 className="text-2xl font-bold text-textPrimary mb-4">Response Format</h2>
          <p className="text-textSecondary mb-4">
            All API responses are in JSON format. Successful responses include the requested data,
            while errors include an error object with details.
          </p>

          <CodeBlock
            code={`// Success response
{
  "success": true,
  "data": {
    "id": "meeting-123",
    "title": "Team Standup",
    "status": "completed"
  }
}

// Error response
{
  "success": false,
  "error": {
    "code": "NOT_FOUND",
    "message": "Meeting not found"
  }
}`}
            language="json"
          />
        </section>

        <section>
          <h2 className="text-2xl font-bold text-textPrimary mb-4">Next Steps</h2>
          <ul className="list-disc list-inside space-y-2 text-textSecondary dark:text-[#858585]">
            <li>
              <Link to="/docs/api/authentication" className="text-gray-900 dark:text-orange-500 underline hover:text-gray-700 dark:hover:text-orange-400">
                Learn about authentication
              </Link>
            </li>
            <li>
              <Link to="/docs/api/endpoints" className="text-gray-900 dark:text-orange-500 underline hover:text-gray-700 dark:hover:text-orange-400">
                Explore API endpoints
              </Link>
            </li>
          </ul>
        </section>

        <DocsActionButtons pageTitle="API Overview" />
      </div>
    </DocsContent>
  );
}

